import React, { useState, useCallback, useRef, useEffect, MutableRefObject } from 'react';

import { Button, Box, Theme, Typography } from "@material-ui/core";
import { createStyles, makeStyles, withStyles, WithStyles } from '@material-ui/core/styles';

import { AlmeMarkdownResponseNext } from "../../AlmeResponse/AlmeResponse";
import { ResponseAction } from "@alme/types/response/ResponseAction";

import surveyStyles from './AlmeSurvey.styles';
import { AlmeApiUtils } from '../../../api/AlmeApiUtils';
import AlmeSurveyDialog from './AlmeSurveyDialog';
import { FocusComponentProps } from '../../../models/ComponentProps';
import { AlmeSurveyConfig } from './AlmeSurveyConfig';
import { useFocusStyles } from '../../FocusVisibleStyle';

export interface SurveyDialogDisplayText {
    headerText: string;
    bodyText: string;
    buttons: {
        acceptButtonText: string;
        declineButtonText: string;
    };
}

function validateInlineSurveyLinks(action: InlineSurveyResponseAction | undefined): boolean {
    const inlineSurveyLinks = action?.MetaData.InlineSurveyLinks;

    if (!inlineSurveyLinks?.No?.ActionType) {
        console.log("Missing 'No' link config or action type");
        return false;
    }
    if (inlineSurveyLinks?.No?.ActionType === "AppEvent" && !(inlineSurveyLinks.No as InlineSurveyLinkAppEventConfig)?.AppeventName) {
        console.log("Missing 'No' link config or appevent name ");
        return false;
    }
    if (!inlineSurveyLinks?.Yes?.ActionType) {
        console.log("Missing 'Yes' link config or action type");
        return false;
    }
    if (inlineSurveyLinks?.Yes?.ActionType === "AppEvent" && !(inlineSurveyLinks.Yes as InlineSurveyLinkAppEventConfig)?.AppeventName) {
        console.log("Missing 'Yes' link config or appevent type");
        return false;
    }
    if ((inlineSurveyLinks?.Yes?.ActionType !== "AppEvent" && inlineSurveyLinks?.Yes?.ActionType !== "ShowSurvey") ||
        (inlineSurveyLinks?.No?.ActionType !== "AppEvent" && inlineSurveyLinks?.No?.ActionType !== "ShowSurvey")) {
        console.log("Invalid Action type provided");
        return false;
    }
    if (!inlineSurveyLinks?.Yes?.ActionType) {
        console.log("Missing 'Yes' link config or action type");
        return false;
    }
    if (!inlineSurveyLinks?.Title) {
        console.log("Missing survey links title");
        return false;
    }

    return true;
}

function validateSurveys(action: InlineSurveyResponseAction | undefined): boolean {
    const surveys = action?.MetaData.Survey;

    for (const id in surveys) {
        const survey = surveys[id];
        if (!survey?.Footer?.SubmitLink?.AppeventName) {
            console.log("Missing survey submit link appevent name");
            return false;
        }
        if (!survey?.Body?.RadioButtons?.radioOptions?.length && !survey?.Body?.FreeformText) {
            console.log("Missing both radio buttons and freeform text, so no way to collect feedback");
            return false;
        }
    }

    return true;
}

function validateSurveyAction(action: InlineSurveyResponseAction | undefined): boolean {
    if (!action) {
        return false;
    }

    return validateInlineSurveyLinks(action) && validateSurveys(action);
}

function getSurveyResponseAction(props: AlmeSurveyProps): InlineSurveyResponseAction | undefined {
    const action = props.response.responseActions?.find(action => action.Name === "InlineSurvey") as InlineSurveyResponseAction;

    return validateSurveyAction(action) ? action : undefined;
}

function modifyAlmeDialogConfig(inlineSurveyConfig: InlineSurveyConfig, dialogConfig: AlmeSurveyConfig, unitLabel: string): AlmeSurveyConfig {
    return {
        survey: {
            dialog: {
                ...dialogConfig.survey.dialog,
                freeformTextMaxLength: inlineSurveyConfig.Body?.FreeformText?.maxLength ?? 0,
                radioButtonOptions: inlineSurveyConfig.Body?.RadioButtons?.radioOptions ?? [],
                submitLinkConfig: {
                    ...inlineSurveyConfig.Footer.SubmitLink,
                    parameters: {
                        ...inlineSurveyConfig.Footer.SubmitLink.parameters,
                        SurveyUnitLabel: unitLabel,
                    },
                },
            },
        },
    };
}

function setSurveyDialogDisplayText(inlineSurveyConfig: InlineSurveyConfig): SurveyDialogDisplayText {
    return {
        headerText: inlineSurveyConfig.Header.Title,
        bodyText: inlineSurveyConfig.Body?.FreeformText?.placeholder ?? "",
        buttons: {
            acceptButtonText: inlineSurveyConfig.Footer.SubmitLink.DisplayText,
            declineButtonText: inlineSurveyConfig.Footer.CancelLink.DisplayText,
        },
    } as SurveyDialogDisplayText;
}

export interface InlineSurveyLinkBaseConfig {
    ActionType: "ShowSurvey" | "AppEvent";
    DisplayText: string;
}

export interface InlineSurveyLinkShowSurveyConfig extends InlineSurveyLinkBaseConfig {
    ActionType: "ShowSurvey";
    SurveyId: string;
}

export interface InlineSurveyLinkAppEventConfig extends InlineSurveyLinkBaseConfig {
    ActionType: "AppEvent";
    AppeventName: string;
    parameters: {
        grade: number;
        gradingComments: string;
        gradingQueue: number;
        gradingReason: string;
        gradingType: string;
        SurveyUnitLabel: string;
    };
}

export interface InlineSurveyConfig {
    Header: {
        Title: string;
    };
    Body: {
        FreeformText: {
            maxLength: number;
            placeholder: string;
        };
        RadioButtons: {
            radioOptions: string[];
        };
    };
    Footer: {
        CancelLink: {
            DisplayText: string;
        };
        SubmitLink: InlineSurveyLinkAppEventConfig;
    };
}

export interface InlineSurveyResponseAction extends ResponseAction {
    Name: 'InlineSurvey';
    MetaData: {
        InlineSurveyLinks: {
            Title: string;
            No: InlineSurveyLinkBaseConfig;
            Yes: InlineSurveyLinkBaseConfig;
        };
        Survey: {
            [key: string]: InlineSurveyConfig;
        };
    };
    SurveyUnitLabel: string;
}

export type FireSurveyAppEvent = (entryId: string,
    appEventName: string,
    grade: number,
    SurveyUnitLabel: string,
    gradingComments?: string,
    gradingQueue?: number,
    gradingReason?: string,
    gradingType?: string) => Promise<void>;

const useStyles = makeStyles(({ spacing }: Theme) =>
    createStyles({
        header: {
            maxHeight: 10,
        },
        closeContainer: {
            display: 'inline-block',
        },
        surveyButtonBox: {
            marginTop: spacing(-0.5),
            padding: spacing(1),
        },
        surveyTitle: {
            display: 'inline',
            paddingRight: 10,
            marginTop: spacing(.5),
        },
    }),
);

interface AlmeSurveyProps extends WithStyles<typeof surveyStyles> {
    response: AlmeMarkdownResponseNext;
    isLastResponse: boolean;
    onSubmitClick: FireSurveyAppEvent;
    setIsDialogOpen: (isOpen: boolean) => void;
    responseId: string;
    focusProps?: FocusComponentProps;
    almeConfig: AlmeSurveyConfig;
    isDialogOpen?: boolean;
    scrollToResponse?: MutableRefObject<boolean>;
}

const AlmeSurvey: React.FC<AlmeSurveyProps> = (props: AlmeSurveyProps) => {
    const [showSurveyDialog, setShowSurveyDialog] = useState<boolean>(false);
    const [selectedSurveyId, setSelectedSurveyId] = useState<string>();
    const [shouldFocus, setShouldFocus] = useState<boolean>(false);
    const surveyTriggerRef = useRef<HTMLButtonElement>(null);

    const classes = useStyles();
    const Focusclasses = useFocusStyles();

    useEffect(() => {
        if (!props.isDialogOpen && shouldFocus) {
            surveyTriggerRef.current?.focus();
            setShouldFocus(false);
        }
    }, [props.isDialogOpen, shouldFocus]);

    const inlineAction = getSurveyResponseAction(props);

    function handleSurveyLinkClick(config: InlineSurveyLinkBaseConfig, SurveyUnitLabel: string): void {

        let typedConfig;
        switch (config.ActionType) {
            case "AppEvent":
                if(props.scrollToResponse)
                    props.scrollToResponse.current = true;
                typedConfig = config as InlineSurveyLinkAppEventConfig;
                void submitSurvey(
                    props.response.extensionData.entryId as string,
                    typedConfig.AppeventName,
                    typedConfig.parameters.grade,
                    SurveyUnitLabel,
                    typedConfig.parameters.gradingComments,
                    typedConfig.parameters.gradingQueue,
                    typedConfig.parameters.gradingReason,
                    typedConfig.parameters.gradingType);

                break;

            case "ShowSurvey":
                typedConfig = config as InlineSurveyLinkShowSurveyConfig;
                setSelectedSurveyId(typedConfig.SurveyId);
                setShowSurveyDialog(true);

                break;
        }
    }

    const submitSurvey = async (entryId: string,
        appEventName: string,
        grade: number,
        SurveyUnitLabel: string,
        gradingComments = "",
        gradingQueue = 2,
        gradingReason = "",
        gradingType = "Survey"): Promise<void> => {
        await props.onSubmitClick(entryId, appEventName, grade, SurveyUnitLabel, gradingComments, gradingQueue, gradingReason, gradingType);

        setShowSurveyDialog(false);
    };

    const handleNoClick = (): void => {

        handleSurveyLinkClick(inlineAction?.MetaData.InlineSurveyLinks.No as InlineSurveyLinkBaseConfig, inlineAction?.SurveyUnitLabel ?? "");
    };

    const handleYesClick = (): void => {
        handleSurveyLinkClick(inlineAction?.MetaData.InlineSurveyLinks.Yes as InlineSurveyLinkBaseConfig, inlineAction?.SurveyUnitLabel ?? "");
    };

    return inlineAction ? (
        <>
            <Box className={classes.surveyButtonBox}>
                <Typography variant={"body1"} align={"left"} color={"inherit"} noWrap={true}
                    className={classes.surveyTitle}>
                    {inlineAction.MetaData.InlineSurveyLinks.Title}
                </Typography>
                <div style={{ display: 'inline-block' }}>
                    <Button
                        id={`alme-iva-card-${props.responseId}-survey-no-button`}
                        className={`${props.classes.surveyNoButton} ${Focusclasses.visibleFocus}`}
                        onClick={handleNoClick}
                        ref={surveyTriggerRef}
                        variant={props.almeConfig.survey.dialog.styles.buttons.variant}
                        color={props.almeConfig.survey.dialog.styles.buttons.color}
                        size={"small"}
                    >
                        {inlineAction.MetaData.InlineSurveyLinks.No.DisplayText}
                    </Button>
                    <Button
                        id={`alme-iva-card-${props.responseId}-survey-yes-button`}
                        className={`${props.classes.surveyYesButton} ${Focusclasses.visibleFocus}`}
                        onClick={handleYesClick}
                        variant={props.almeConfig.survey.dialog.styles.buttons.variant}
                        color={props.almeConfig.survey.dialog.styles.buttons.color}
                        size={"small"}
                    >
                        {inlineAction.MetaData.InlineSurveyLinks.Yes.DisplayText}
                    </Button>
                </div>
            </Box>
            {selectedSurveyId ?
                (<AlmeSurveyDialog
                    almeConfig={modifyAlmeDialogConfig(inlineAction.MetaData.Survey[selectedSurveyId], props.almeConfig, inlineAction.SurveyUnitLabel)}
                    surveyDialogDisplayText={setSurveyDialogDisplayText(inlineAction.MetaData.Survey[selectedSurveyId])}
                    isOpen={showSurveyDialog}
                    setIsDialogOpen={props.setIsDialogOpen}
                    onSubmitSurvey={submitSurvey}
                    entryId={props.response.extensionData.entryId as string}
                    scrollToResponse={props.scrollToResponse}
                />) :
                <></>
            }
        </>
    ) : null;
};

export default withStyles(surveyStyles)(AlmeSurvey);

export function OnSurveySubmitClick(apiUtils: React.MutableRefObject<AlmeApiUtils>): FireSurveyAppEvent {
    return useCallback(async (
        entryId: string,
        appEventName: string,
        grade: number,
        SurveyUnitLabel: string,
        gradingComments = "",
        gradingQueue = 2,
        gradingReason = "",
        gradingType = "Survey"): Promise<void> => {
        const parameters = {
            entryId,
            grade,
            SurveyUnitLabel,
            gradingComments,
            gradingQueue,
            gradingReason,
            gradingType,
        };

        await apiUtils.current.submitAppEvent(appEventName, 'External', parameters);

    }, [apiUtils]);
}
